import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import InteractiveClinicMap from "../components/InteractiveClinicMap";
import { hasCoordinates } from "../mapbox";
import type { VerifiedAddress } from "../types";
import styles from "./ClinicOnboardingMapPage.module.css";

function parseCoordinate(value: string | null) {
  if (!value) {
    return null;
  }

  const parsedValue = Number.parseFloat(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

const ClinicOnboardingMapPage = () => {
  const [searchParams] = useSearchParams();
  const latitude = parseCoordinate(searchParams.get("lat"));
  const longitude = parseCoordinate(searchParams.get("lng"));
  const formattedAddress = searchParams.get("label")?.trim() || "";

  const address: VerifiedAddress | null =
    latitude !== null && longitude !== null
      ? {
          formattedAddress,
          line1: "",
          city: "",
          state: "",
          postalCode: "",
          country: "",
          placeId: "interactive-map-view",
          latitude,
          longitude,
          verificationSource: "mapbox-geocoding",
        }
      : null;

  return (
    <div className={styles.page}>
      <section className={styles.shell}>
        <header className={styles.header}>
          <div>
            <p className={styles.eyebrow}>Clinic Map</p>
            <h1 className={styles.title}>Interactive location view</h1>
            <p className={styles.address}>
              {formattedAddress || "Selected clinic address"}
            </p>
          </div>

          <div className={styles.toolbar}>
            <Link
              to="/"
              className={`${styles.btn} ${styles.btnGhost}`}
            >
              Back to form
            </Link>
            {hasCoordinates(address) ? (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={() => window.close()}
              >
                Close window
              </button>
            ) : null}
          </div>
        </header>

        {hasCoordinates(address) ? (
          <InteractiveClinicMap
            address={address}
            className={styles.mapSurface}
            initialZoom={15}
            showControls={true}
            showFullscreenControl={true}
          />
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyCopy}>
              <h2 className={styles.emptyTitle}>Map unavailable</h2>
              <p className={styles.emptyText}>
                The selected clinic address did not include enough coordinate
                data to open the interactive map.
              </p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default ClinicOnboardingMapPage;
